import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin, ArrowRight, Sparkles, User, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/SiteLayout";
import { cn } from "@/lib/utils";
import { getLinkedInAvatarUrl } from "@/api/linkedinService";

/**
 * ============================================================
 * FEATURED WORKSHOP SHOWCASE
 * ============================================================
 * Prominent visual presentation of the flagship upcoming workshop.
 * Directs interested developers into /workshops/:slug.
 */
export default function FeaturedWorkshop({ workshop }) {
  if (!workshop) return null;

  return (
    <section className="mb-14">
      <Reveal delay={120}>
        <div className="relative group">
          {/* Subtle Ambient Hover Glow */}
          <div className="absolute -inset-1 rounded-[2.5rem] bg-gradient-to-r from-primary/25 via-primary/10 to-transparent opacity-40 blur-xl transition-opacity duration-500 group-hover:opacity-100" />

          {/* Card Container */}
          <div className="relative rounded-3xl border border-white/15 bg-white/[0.04] backdrop-blur-2xl overflow-hidden shadow-[0_24px_70px_rgba(0,0,0,0.5)] transition-all duration-300 hover:border-primary/40">
            <div className="grid grid-cols-1 lg:grid-cols-12">
              {/* Left Column: Visual Cover & Overlay Badges (5 cols) */}
              <div className="lg:col-span-5 relative min-h-[280px] sm:min-h-[340px] lg:min-h-full overflow-hidden bg-black/40">
                <img
                  src={workshop.cover_image}
                  alt={workshop.title}
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  onError={(e) => {
                    // Fallback in case of broken network image
                    e.target.src =
                      "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1200&auto=format&fit=crop&q=80";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-black/80" />

                {/* Badges on Top of Cover */}
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/90 text-black text-xs font-bold tracking-wide shadow-lg shadow-primary/30">
                    <Sparkles className="w-3 h-3" />
                    <span>Featured Event</span>
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-black/70 border border-white/20 text-white text-xs font-medium backdrop-blur-md">
                    {workshop.category}
                  </span>
                </div>

                {/* Price pill on bottom of cover */}
                <div className="absolute bottom-4 left-4">
                  {workshop.is_free ? (
                    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/20 border border-green-500/30 text-xs font-semibold backdrop-blur-md">
                      <span className="text-white/50 line-through font-normal">₹999</span>
                      <span className="text-green-400">FREE</span>
                    </div>
                  ) : (
                    <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-xs font-semibold backdrop-blur-md">
                      ₹{workshop.price}
                    </span>
                  )}
                </div>
              </div>

              {/* Right Column: Workshop Details & CTA (7 cols) */}
              <div className="lg:col-span-7 p-6 sm:p-8 lg:p-10 flex flex-col justify-between space-y-6">
                <div>
                  {/* Category & Mode Strip */}
                  <div className="flex items-center gap-3 text-xs text-white/50 mb-2.5">
                    <span className="text-primary font-semibold">{workshop.mode}</span>
                    <span>•</span>
                    <span>{workshop.duration}</span>
                    {workshop.spots_remaining > 0 && (
                      <>
                        <span>•</span>
                        <span className="text-green-400 font-medium">
                          {workshop.spots_remaining} seats remaining
                        </span>
                      </>
                    )}
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white group-hover:text-primary transition-colors leading-tight mb-3">
                    {workshop.title}
                  </h2>

                  {/* Short Description */}
                  <p className="text-xs sm:text-sm text-white/70 leading-relaxed line-clamp-3">
                    {workshop.short_description}
                  </p>

                  {/* Key Logistics Meta Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-6 mt-6 border-t border-white/10 text-xs">
                    <div>
                      <div className="text-[11px] text-white/45 mb-0.5 flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-primary" />
                        <span>Date</span>
                      </div>
                      <div className="font-semibold text-white">{workshop.date}</div>
                    </div>

                    <div>
                      <div className="text-[11px] text-white/45 mb-0.5 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-primary" />
                        <span>Schedule</span>
                      </div>
                      <div className="font-semibold text-white">
                        {workshop.start_time} - {workshop.end_time}
                      </div>
                    </div>

                    <div className="col-span-2 sm:col-span-1">
                      <div className="text-[11px] text-white/45 mb-0.5 flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-primary" />
                        <span>Format</span>
                      </div>
                      <div className="font-semibold text-white truncate">
                        {workshop.mode === "Online"
                          ? "Interactive Live Session"
                          : workshop.venue || "Hybrid"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Row: Instructor & Action CTA */}
                <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  {/* Instructor Preview */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl overflow-hidden border border-white/15 bg-white/5 flex-shrink-0">
                      {workshop.instructor?.avatar_url || workshop.instructor?.linkedin_url ? (
                        <img
                          src={workshop.instructor?.avatar_url || getLinkedInAvatarUrl(workshop.instructor?.linkedin_url)}
                          alt={workshop.instructor.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                            const fallback = e.target.nextSibling;
                            if (fallback) fallback.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className={`w-full h-full items-center justify-center text-primary ${
                          workshop.instructor?.avatar_url || workshop.instructor?.linkedin_url ? "hidden" : "flex"
                        }`}
                      >
                        <User className="w-5 h-5" />
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-white">
                        {workshop.instructor?.name}
                      </div>
                      <div className="text-[10px] text-white/50 truncate max-w-[220px]">
                        {workshop.instructor?.role}
                      </div>
                    </div>
                  </div>

                  {/* Primary CTA */}
                  <Link to={`/workshops/${workshop.slug}`}>
                    <Button
                      className={cn(
                        "w-full sm:w-auto h-11 px-6 rounded-xl bg-primary text-black font-semibold text-xs",
                        "hover:bg-primary/90 transition-all duration-200 gap-2 btn-glow",
                        "shadow-[0_0_20px_rgba(0,194,255,0.3)]"
                      )}
                    >
                      <span>View Workshop</span>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
